import React from "react";
import PropTypes from "prop-types";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import AppUtils from "@src/utils/appUtils";
import CardContainer from "@src/components/cards/CardContainer";
import HorizontalLayout from "@src/components/cards/HorizontalLayout";
import {
  ExcludedBadgeCircular,
  SectionedProgressBarCircular,
} from "@src/components/atoms";
import { useDCContext } from "@src/context/dcContext";
import VerticalLayout from "@src/components/cards/VerticalLayout";
import HorizontalResponsiveLayout from "@src/components/cards/HorizontalResponsiveLayout";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const GoalResultCard = (props) => {
  const {
    targetGoal,
    targetActual, //=target.succeeded
    targetAchieved,
    industry,
    preferredEventName,
    preferredEventNamePlural,
    customiseGoalReason,
    behaviourAcronym,
    isOpen,
    isCurrent,
    position,
    periodName = "period",
    baselines = [],
    recentAverageType,
    baselineDateSource,
  } = props;

  const dcContext = useDCContext();
  const { getDynamicReplacement: getDynamicContent } = dcContext;
  const dynamicStyles = getBehaviourColourForPosition(position);

  const targetGoalValid = targetGoal !== null && targetGoal > 0;
  const targetExceeded = targetAchieved && targetActual >= targetGoal + 1;
  let textState = "";
  if (targetAchieved) {
    textState = targetExceeded ? "succeeded_extra" : "succeeded";
  } else if (!!isCurrent) {
    textState = "current";
  } else if (!!isOpen) {
    textState = "open";
  } else {
    textState = "closed_failed";
  }
  let contentText = `captain__targets__page__title_${textState}`;

  if (!targetGoalValid) {
    contentText = "captain__targets__events__invalid";
  }

  let eventName = null;
  let targetActualAdjusted = targetActual;
  let targetGoalAdjusted = targetGoal;

  if (!!customiseGoalReason) {
    contentText = `captain__targets__page__title_${customiseGoalReason}`;
    eventName =
      customiseGoalReason === "one"
        ? eventName
        : industry.toLowerCase() === "aviation"
          ? "flying"
          : "sailing";
    targetActualAdjusted = customiseGoalReason === "one" ? 1 : targetActual;
    targetGoalAdjusted = customiseGoalReason === "one" ? 1 : targetGoal;
  }

  const neutralColour = getBehaviourColourForPosition(0);
  const goalColour =
    !customiseGoalReason || customiseGoalReason === "one"
      ? dynamicStyles.codes["500"]
      : customiseGoalReason === "open_untargeted"
        ? "#808080"
        : "#000000";

  const title = (
    <div>
      <div className="font-bold my-3">
        {!!behaviourAcronym &&
          getDynamicContent(contentText, {
            PROGRESS: targetActualAdjusted,
            TARGET: targetGoalAdjusted,
            COLOUR: goalColour,
            EVENT_NAME: !!eventName
              ? eventName
              : targetActualAdjusted == 1
                ? preferredEventName
                : preferredEventNamePlural,
            PERIOD_NAME: periodName.toLowerCase(),
          })}
      </div>
    </div>
  );
  const histGoal =
    baselines?.find((b) => b.behaviourAcronym === behaviourAcronym)
      ?.historicalSuccessEvents || 1;

  const recentAverageTypeSuffix =
    recentAverageType === "CUSTOMER_AVG" ? "_customer_avg" : "";
  const baselineDateSourceSuffix = baselineDateSource
    ? `_${baselineDateSource}`
    : "";
  const subtitleContentText = `captain__targets__page__subtitle${recentAverageTypeSuffix}${baselineDateSourceSuffix}`;

  console.log(baselineDateSource, recentAverageTypeSuffix);
  console.log(subtitleContentText);

  const subtitle = (
    <div>
      <div className=" my-3">
        {!!behaviourAcronym &&
          getDynamicContent(subtitleContentText, {
            TARGET: histGoal,
            EVENT_NAME:
              histGoal == 1 ? preferredEventName : preferredEventNamePlural,
            PERIOD_NAME: periodName.toLowerCase(),
          })}
      </div>
    </div>
  );

  return (
    <CardContainer px={4} shadow={false}>
      <HorizontalResponsiveLayout
        horizontal={{ ratio: [1, 1] }}
        vertical={{ className: "p-3" }}
      >
        {/* child 1 - left side */}
        <div className="py-4">
          {!customiseGoalReason || customiseGoalReason === "one" ? (
            <SectionedProgressBarCircular
              size="big"
              targetAchieved={targetAchieved}
              bgColor={targetAchieved && dynamicStyles.codes["300"]}
              successValue={targetActualAdjusted}
              empty={!targetGoalValid}
              goalValue={targetGoalAdjusted}
              eventName={eventName?.toLowerCase()}
              lineColor={dynamicStyles.codes["500"]}
              successColor={dynamicStyles.codes["400"]}
              parentColor={
                targetAchieved ? "transparent" : dynamicStyles.codes["200"]
              }
              colorName={dynamicStyles.name}
              behaviourAcronym={behaviourAcronym}
            />
          ) : customiseGoalReason === "open_untargeted" ? (
            <SectionedProgressBarCircular
              size="big"
              targetAchieved={targetAchieved}
              bgColor={targetAchieved && neutralColour.codes["300"]}
              empty={!targetGoalValid}
              successValue={targetActual}
              goalValue={targetGoal}
              eventName={eventName?.toLowerCase()}
              lineColor={neutralColour.codes["500"]}
              successColor={neutralColour.codes["400"]}
              parentColor={
                targetAchieved ? "transparent" : neutralColour.codes["200"]
              }
              colorName={neutralColour.name}
              behaviourAcronym={behaviourAcronym}
            />
          ) : (
            <ExcludedBadgeCircular size={"big"} />
          )}
        </div>
        {/* child 2 - right side */}
        <div className=" flex flex-col justify-center w-full h-full pr-4">
          <div className="text-left">{title}</div>
          <div className="text-left">{subtitle}</div>
        </div>
      </HorizontalResponsiveLayout>
    </CardContainer>
  );
};

export const goalResultCardControls = {
  industry: COMMON_CONTROLS.industry,
  preferredEventName: {
    propType: PropTypes.string,
    description: "Event name",
    controlType: "text",
  },
  preferredEventNamePlural: {
    propType: PropTypes.string,
    description: "Event name - plural",
    controlType: "text",
  },
  periodLabel: {
    propType: PropTypes.string,
    description: "Period type",
    controlType: "text",
  },
  behaviourAcronym: COMMON_CONTROLS.behaviour_acronym,
  customiseGoalReason: {
    propType: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    description: "Customised string for special goal statements",
    controlType: "select",
    options: ["one", "none", null],
  },
  getDynamicContent: {
    propType: PropTypes.func,
    description: "Function to get dynamic content",
    disable: true,
  },
  handleClick: {
    propType: PropTypes.func,
    description: "Click handler for the card",
    disable: true,
  },
  targetGoal: {
    propType: PropTypes.number,
    description: "Number of goals set for the period",
    controlType: "number",
  },
  targetActual: {
    propType: PropTypes.number,
    description: "Number of goals achieved in the period",
    controlType: "number",
  },
  targetAchieved: {
    propType: PropTypes.bool,
    description: "Target was achieved or not",
    controlType: "boolean",
    defaultValue: true,
  },
  isPresent: {
    propType: PropTypes.bool,
    description: "is open period",
    controlType: "boolean",
    defaultValue: true,
  },
  position: {
    propType: PropTypes.number,
    description: "Behaviour position in this team/customer",
    controlType: "number",
    min: 1,
    max: 5,
  },
  baseline: {
    propType: PropTypes.object,
    description: "Baseline info",
    controlType: "text",
  },
  recentAverageType: {
    propType: PropTypes.string,
    description:
      "When 'CUSTOMER_AVG', the subtitle DC key gets a '_customer_avg' suffix instead of the operator's own historical baseline",
    controlType: "select",
    options: [null, "CUSTOMER_AVG"],
  },
  baselineDateSource: {
    propType: PropTypes.string,
    description:
      "Where the baseline date range came from (e.g. 'target', 'team', 'customer_contract'). Appended as a suffix to the subtitle DC key, combined with recentAverageType, so each combination can resolve to its own DC copy (e.g. 'captain__targets__page__subtitle_customer_avg_team').",
    controlType: "select",
    options: [null, "target", "team", "customer_contract"],
  },
};

GoalResultCard.propTypes = AppUtils.getPropTypeFromControls(
  goalResultCardControls,
);

export default GoalResultCard;
